import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
    interface Window {
        gtag: (...args: any[]) => void;
    }
}

interface GoogleAnalyticsProps {
    trackingId?: string;
}

export function GoogleAnalytics({ trackingId }: GoogleAnalyticsProps) {
    const location = useLocation();

    useEffect(() => {
        if (!trackingId) return;

        // Load Google Analytics script
        const script1 = document.createElement('script');
        script1.async = true;
        script1.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
        document.head.appendChild(script1);

        const script2 = document.createElement('script');
        script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${trackingId}', {
        page_title: document.title,
        page_location: window.location.href,
      });
    `;
        document.head.appendChild(script2);

        return () => {
            document.head.removeChild(script1);
            document.head.removeChild(script2);
        };
    }, [trackingId]);

    // Track page views on route changes
    useEffect(() => {
        if (!trackingId || !window.gtag) return;

        window.gtag('config', trackingId, {
            page_title: document.title,
            page_location: window.location.href,
            page_path: location.pathname + location.search,
        });
    }, [location, trackingId]);

    return null;
}

// Custom hook for tracking events
export function useAnalytics() {
    const trackEvent = (action: string, category: string, label?: string, value?: number) => {
        if (window.gtag) {
            window.gtag('event', action, {
                event_category: category,
                event_label: label,
                value: value,
            });
        }
    };

    const trackPageView = (page_title: string, page_location: string) => {
        if (window.gtag) {
            window.gtag('event', 'page_view', {
                page_title,
                page_location,
            });
        }
    };

    const trackConversion = (transactionId: string, value: number, currency = 'USD') => {
        if (window.gtag) {
            window.gtag('event', 'purchase', {
                transaction_id: transactionId,
                value: value,
                currency: currency,
            });
        }
    };

    return {
        trackEvent,
        trackPageView,
        trackConversion,
    };
}